import type { CourtageNotEligibleReason } from './policy';
/**
 * Commission wire types — string-union mirrors of the Epic-11 pgEnums.
 *
 * Story 11.1: wire-type unions for settlement_type, commission_kind, settlement_rate,
 * match_level, commission_line_status, statement_source_type, code_table_type, code_status.
 *
 * Mirrors packages/shared/src/invoice.ts (same convention):
 *   - String-union type per pgEnum (not a const-object — tree-shakeable + cross-app safe)
 *   - Values match the pgEnum values EXACTLY (single source of truth is the DB enum)
 *
 * [Source: packages/shared/src/invoice.ts] (string-union convention)
 * [Source: packages/db/src/schema/_enums.ts] (pgEnum declarations — 11.1 block)
 * [Source: docs/prd/epic-11-courtageabrechnung.md#11.1]
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#3+12]
 */

// ── Abrechnungsart (settlement type) ─────────────────────────────────────────

/**
 * SettlementType — IGB2B Abrechnungsart codes (Kap. 3.3).
 *
 * '01' — Neugeschäft / Erstabrechnung
 * '02' — Abgang / Storno (negative Rückbelastung)
 * '03' — Technische Mutation (delta-premium based)
 * '04' — übrige / laufend (periodic)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#3.3]
 */
export type SettlementType = '01' | '02' | '03' | '04';

// ── Provisionsart (commission kind) ──────────────────────────────────────────

/**
 * CommissionKind — IGB2B Provisionsart codes (Kap. 3.3).
 *
 * '01' — Courtage / Betreuungsprovision
 * '02' — Einmalige Abschlussprovision
 * '03' — Laufende Abschlussprovision
 * '04' — Pauschalprovision (Fixbetrag)
 * '05' — Superprovision
 * '06' — Akontocourtage / Akontoprovision (D11-AKONTO-NOT-A-DIFFERENCE)
 * '09' — übrige Provisionsarten
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#3.3]
 */
export type CommissionKind = '01' | '02' | '03' | '04' | '05' | '06' | '09';

// ── Abrechnungsrate (settlement rate / Periodizität) ─────────────────────────

/**
 * SettlementRate — IGB2B Abrechnungsrate codes (Kap. 3.3).
 *
 * '01' — jährlich
 * '02' — halbjährlich
 * '03' — quartalsweise
 * '04' — monatlich
 * '05' — wöchentlich
 * '06' — einmalig
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#3.3]
 */
export type SettlementRate = '01' | '02' | '03' | '04' | '05' | '06';

// ── Match level (L1–L4 four-level tolerance) ─────────────────────────────────

/**
 * MatchLevel — the four-level tolerance classification for Courtage lines (Kap. 8.2).
 *
 * L1 — Exakt: Δ=0 / ≤ rounding tolerance → auto-match released
 * L2 — Toleranz: Δ ≤ configured CHF/% → auto-match logged
 * L3 — Abweichung: Δ over tolerance → "Courtagedifferenz prüfen" Task
 * L4 — Strukturell: policy missing / no agreement / wrong broker-code → hard-exception
 *
 * Mirrors the four-level K0–K3 of Epic-10 (the same routing primitive).
 * Priority: Unterzahlung (Ist < Soll) is the critical direction.
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#8.2]
 */
export type MatchLevel = 'L1' | 'L2' | 'L3' | 'L4';

// ── Commission line status ────────────────────────────────────────────────────

/**
 * CommissionLineStatus — the per-line Statusfolge (Kap. 8.3).
 *
 * pending          — received, not yet processed
 * matched          — Police-Identifikation + Berechtigung passed (Story 11.4)
 * deviation_open   — L3 deviation detected (Ist ≠ Soll outside tolerance)
 * in_clarification — deviation under clarification with carrier
 * resolved         — deviation resolved (accepted / corrected)
 * written_off      — deviation written off (Abschreibung)
 * reclaimed        — overpayment reclaimed (Rückbelastung)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#8.3]
 */
export type CommissionLineStatus =
  | 'pending'
  | 'matched'
  | 'deviation_open'
  | 'in_clarification'
  | 'resolved'
  | 'written_off'
  | 'reclaimed';

// ── Statement source type ─────────────────────────────────────────────────────

/**
 * StatementSourceType — who sent the commission statement (Kap. 12 + Kap. 10).
 *
 * 'carrier'      — Versicherer sent directly (standard pipeline)
 * 'lead_broker'  — Lead-Broker forwarded (Empfänger-Rolle, Story 11.8)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export type StatementSourceType = 'carrier' | 'lead_broker';

// ── Code table type ───────────────────────────────────────────────────────────

/**
 * CodeTableType — which IGB2B code table a code_table_entry row belongs to (Kap. 11.5).
 *
 * 'settlement_type' — Abrechnungsart codes
 * 'commission_kind' — Provisionsart codes
 * 'settlement_rate' — Abrechnungsrate codes
 * 'igb2b_branch'    — IGB2B-Branchencode (e.g. '91000' = Leben)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#11.5]
 */
export type CodeTableType = 'settlement_type' | 'commission_kind' | 'settlement_rate' | 'igb2b_branch';

// ── Code status ───────────────────────────────────────────────────────────────

/**
 * CodeStatus — lifecycle status of a code_table_entry (Kap. 11.5).
 *
 * 'active'         — known code, internally mapped (default for known codes)
 * 'pending_review' — unknown/new code auto-extracted → broker must map before it drives checks
 *                    (no silent mis-match — D11-CODE-TABLE-AUTO-CAPTURE)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#11.5]
 */
export type CodeStatus = 'active' | 'pending_review';

// ── Story 11.2 additions: new enum wire types + DTOs ─────────────────────────

/**
 * CommissionProvisioningType — one_time vs. recurring provision (Kap. 12).
 *
 * 'one_time'  — Abschlussprovision (one-time at policy inception)
 * 'recurring' — Betreuungscourtage / laufende Provision (per period)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export type CommissionProvisioningType = 'one_time' | 'recurring';

/**
 * CommissionType — how the tariff rate is expressed (Kap. 12).
 *
 * 'percent' — rate is a percentage of the basis amount
 * 'fixed'   — fixed_amount in CHF (Pauschalprovision)
 * 'tiered'  — tiered scale (tiers_json carries the scale table)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export type CommissionType = 'percent' | 'fixed' | 'tiered';

/**
 * TariffStatus — lifecycle of a commission_tariff row (Kap. 11.3).
 *
 * 'extracted'  — freshly extracted from the broker agreement (draft; does NOT drive the check)
 * 'in_review'  — edited by the broker (draft; does NOT drive the check)
 * 'approved'   — explicitly approved; ONLY approved tariffs drive the Soll computation (AC4)
 * 'superseded' — superseded by a newer agreement for the same scope
 *
 * D11-TARIFF-SETTINGS-AREA: ONLY 'approved' tariffs are returned by getApprovedTariffsInPeriod.
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#11.3]
 */
export type TariffStatus = 'extracted' | 'in_review' | 'approved' | 'superseded';

/**
 * AgreementSource — which intake channel produced the agreement_document (Kap. 12).
 *
 * 'post'   — scanned from Posteingang (via intake + doc-pipeline)
 * 'upload' — uploaded via the broker dashboard (AgreementUploadController)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export type AgreementSource = 'post' | 'upload';

/**
 * AgreementDocumentDto — one agreement_document row as persisted (Kap. 12).
 *
 * Returned by the settings area GET (list / detail).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export interface AgreementDocumentDto {
  agreement_doc_id:  string;
  tenant_id:         string;
  carrier_id:        string | null;
  source:            AgreementSource;
  document_id:       string | null;
  received_at:       string;
  extraction_status: string;
  version:           number;
  created_at:        string;
  updated_at:        string;
}

/**
 * CommissionTariffDto — one commission_tariff row as returned to the broker (Kap. 12).
 *
 * Finance-only fields (rate, fixed_amount, tiers_json) are null for non-Finance roles
 * (D11-FIELD-LEVEL-FINANCE-ONLY — gated at READ time in NestJS via CommissionFinanceGate).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export interface CommissionTariffDto {
  tariff_id:          string;
  agreement_doc_id:   string;
  tenant_id:          string;
  carrier_id:         string | null;
  product:            string | null;
  commission_kind:    CommissionKind | null;
  provisioning_type:  CommissionProvisioningType | null;
  commission_type:    CommissionType | null;
  // Finance-only (nulled for non-Finance — D11-FIELD-LEVEL-FINANCE-ONLY):
  rate:               string | null;   // numeric(8,6) → string
  fixed_amount:       string | null;   // numeric(14,2) → string
  tiers_json:         unknown | null;
  calculation_basis:  string | null;   // 'gross' | 'net'
  status:             TariffStatus;
  confidence:         string | null;   // numeric(5,4) → string
  approved_by:        string | null;
  approved_at:        string | null;
  valid_from:         string | null;   // ISO date string
  valid_to:           string | null;   // ISO date string
  created_at:         string;
  updated_at:         string;
}

// ── Commission statement DTO ──────────────────────────────────────────────────

/**
 * CommissionStatementDto — the statement head (Abrechnungskopf) as persisted in the DB.
 *
 * Used as a response DTO for the commission controller (read-only; no PII amounts at the
 * statement level — Finance-only gate applies to line amounts via the field-level guard).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export interface CommissionStatementDto {
  statement_id:       string;
  tenant_id:          string;
  carrier_id:         string | null;
  broker_vu_code:     string | null;
  broker_finma_nr:    string | null;
  period_from:        string | null;   // ISO date string
  period_to:          string | null;   // ISO date string
  currency:           string;
  total_amount:       string | null;   // numeric(14,2) → string
  source_type:        StatementSourceType;
  source_document_id: string | null;
  check_score:        string | null;   // numeric(5,4) → string
  status:             string;
  created_at:         string;
  updated_at:         string;
}

// ── Commission statement line DTO ─────────────────────────────────────────────

/**
 * CommissionStatementLineDto — one Belegzeile as persisted.
 *
 * courtage amounts (basis_amount, rate, factor, line_total, expected_amount, delta_chf, delta_pct)
 * are Finance-only fields (D11-FIELD-LEVEL-FINANCE-ONLY) — the controller/service applies
 * field-level gating; this DTO carries the full structure but callers receive null for
 * restricted fields when the user lacks the Finance role.
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#12]
 */
export interface CommissionStatementLineDto {
  line_id:           string;
  statement_id:      string;
  tenant_id:         string;
  policy_id:         string | null;
  policy_version_id: string | null;
  policy_number_raw: string | null;
  // holder_raw intentionally omitted from DTO — PII; never logged/returned without Finance gate
  line_period_from:  string | null;   // ISO date string
  line_period_to:    string | null;   // ISO date string
  settlement_type:   SettlementType | null;
  settlement_rate:   SettlementRate | null;
  igb2b_branch:      string | null;
  product_label:     string | null;
  commission_kind:   CommissionKind | null;
  // Finance-only amounts (nulled out for non-Finance roles by the service/controller):
  basis_amount:      string | null;   // numeric(14,2) → string
  rate:              string | null;   // numeric(8,6) → string
  factor:            string | null;   // numeric(8,6) → string
  line_total:        string | null;   // numeric(14,2) → string (Ist)
  expected_amount:   string | null;   // numeric(14,2) → string (Soll)
  delta_chf:         string | null;   // numeric(14,2) → string
  delta_pct:         string | null;   // numeric(8,4) → string
  match_level:       MatchLevel | null;
  status:            CommissionLineStatus;
  tariff_id:         string | null;
  created_at:        string;
}

// ── Code table entry DTO ──────────────────────────────────────────────────────

/**
 * CodeTableEntryDto — one code_table_entry row as returned to the broker (Kap. 11.5).
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#11.5]
 */
export interface CodeTableEntryDto {
  code_id:           string;
  tenant_id:         string;
  table_type:        CodeTableType;
  code:              string;
  label:             string | null;
  internal_handling: string | null;
  status:            CodeStatus;
  origin:            string;   // 'auto_extracted' | 'manual'
  created_at:        string;
  updated_at:        string;
}

// ── Story 11.7: Courtage-Erwartung + Reconciliation Kennzahlen ───────────────

/**
 * CommissionExpectationStatus — the lifecycle status of a commission expectation (Kap. 9.1).
 *
 * 'expected'  — generated from Bestand; no billed statement received yet (offene Posten)
 * 'received'  — a matching billed line was found (matched_line_id set)
 * 'disputed'  — the billed amount is under dispute / in Klärung
 * 'overdue'   — past due_date still in 'expected' → Task raised automatically
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#9.1]
 */
export type CommissionExpectationStatus = 'expected' | 'received' | 'disputed' | 'overdue' | 'zurueckgezogen';

/**
 * The expectation statuses as values. 'zurueckgezogen' (VJ-145): the period lies outside the
 * policy's courtage window (a termination ended it, a registration was rejected or corrected); the row is
 * kept and comes back to 'expected' when the window reopens. Not counted as outstanding.
 */
export const COMMISSION_EXPECTATION_STATUS = {
  EXPECTED: 'expected',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  OVERDUE: 'overdue',
  ZURUECKGEZOGEN: 'zurueckgezogen',
} as const satisfies Record<string, CommissionExpectationStatus>;

/** The German labels of the expectation statuses (one map for the api and the web). */
export const COMMISSION_EXPECTATION_STATUS_LABELS: Record<CommissionExpectationStatus, string> = {
  expected: 'Erwartet',
  received: 'Eingegangen',
  disputed: 'In Klärung',
  overdue: 'Überfällig',
  zurueckgezogen: 'Zurückgezogen',
};

/** Activity meta event of a withdrawn expectation. */
export const COMMISSION_EXPECTATION_EVENTS = {
  WITHDRAWN: 'commission.expectation_withdrawn',
  RESTORED: 'commission.expectation_restored',
} as const;

/**
 * CommissionExpectationDto — one commission_expectation row (Kap. 12 / Kap. 9.1).
 *
 * Finance-only field: expected_amount (nulled for non-Finance callers via CommissionFinanceGate).
 *
 * [Source: docs/stories/11.7-monitoring-reconciliation.md#Dev Notes — Data Models]
 */
export interface CommissionExpectationDto {
  id:                  string;             // expectation_id (PK)
  tenant_id:           string;
  policy_id:           string;
  period_from:         string;             // ISO date
  period_to:           string;             // ISO date
  commission_kind:     string;             // CommissionKind text value ('01'–'09')
  expected_amount:     string | null;      // Finance-only — numeric(14,2) as string; null when gated
  status:              CommissionExpectationStatus;
  policy_version_id:   string | null;      // FINMA/FIDLEG reproducibility pin
  tariff_id:           string | null;      // FIDLEG reproducibility pin
  matched_line_id:     string | null;      // set when status = 'received'
  task_id:             string | null;      // set when status = 'overdue'
  due_date:            string | null;      // ISO date; optional Frist for overdue check
  created_at:          string;
  updated_at:          string;
}

/**
 * ReconciliationKennzahlenDto — the Periodenabschluss Kennzahlen read-API response (Kap. 9.3).
 *
 * Finance-only CHF fields (nulled for non-Finance callers):
 *   ausstehende_courtage_chf, offene_differenzen_chf, offene_akonti_chf
 *
 * NOT gated (carry no franc figure):
 *   offene_differenzen_count, reconciliation_quote_pct, ueberfaellige_erwartungen
 *
 * 11.8 EXTENSION: the co-broker Kennzahlen below are NOT built here — seam reserved.
 *
 * [Source: docs/stories/11.7-monitoring-reconciliation.md#Dev Notes — API]
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#9.3]
 */
export interface ReconciliationKennzahlenDto {
  period_from:                  string;       // ISO date (the period queried)
  period_to:                    string;       // ISO date (the period queried)

  // Finance-only (nulled for non-Finance callers — D11-FIELD-LEVEL-FINANCE-ONLY / revDSG)
  ausstehende_courtage_chf:     string | null; // Σ expected_amount of NOT-received expectations
  offene_differenzen_chf:       string | null; // Σ |delta_chf| (open-difference magnitude) of latest exceptions in deviation_open|in_clarification (GAP-B5: ABS, not a signed net)
  offene_akonti_chf:            string | null; // Σ open_balance of non-verrechnet akonto

  // NOT gated (counts/% carry no franc figure)
  offene_differenzen_count:     number;        // count of latest open exceptions
  reconciliation_quote_pct:     number;        // machine-matched share % = (L1/L2 matched ∪ received) / total
  ueberfaellige_erwartungen:    number;        // count of expectations in 'overdue'

  // 11.8 EXTENSION — co-broker both-sides monitoring Kennzahlen (Kap. 10.8):
  offene_cobroker_verbindlichkeiten_chf: string | null; // CHF still owed to recipients (lead payout_due/mismatch) [Finance-only]
  offene_cobroker_forderungen_chf:       string | null; // CHF still owed from lead (recipient under-received) [Finance-only]
  anteilsabweichungen_count:             number;         // count of mismatch split rows (both roles) [NOT gated]
  anteilsabweichungen_chf:               string | null; // Σ |delta_chf| of mismatch rows (both roles) [Finance-only]
  nicht_weitergeleitete_splits_chf:      string | null; // Σ lead expected_share of lead_not_forwarded rows [Finance-only]
}

// ── Story 11.8: Co-Broker types + DTOs ────────────────────────────────────────

/**
 * BrokerRoleKind — the role the own tenant plays for a given policy/mandate (Kap. 10.1).
 *
 * 'sole'      — sole broker; no split pipeline
 * 'lead'      — lead broker; receives carrier statement and forwards a share
 * 'recipient' — recipient broker; receives the forwarded share from the lead
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.1+12]
 */
export type BrokerRoleKind = 'sole' | 'lead' | 'recipient';

/**
 * CobrokerScope — the scope of a co-broker agreement (Kap. 10.2).
 *
 * 'policy'    — agreement applies to one specific policy
 * 'mandate'   — agreement applies to all policies under a mandate
 * 'portfolio' — agreement applies to the entire portfolio with this counterparty
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.2+12]
 */
export type CobrokerScope = 'policy' | 'mandate' | 'portfolio';

/**
 * SplitMethod — how the split share is computed (Kap. 10.2 / Kap. 10.3).
 *
 * 'percent' — a percentage of the basis (split_value = rate e.g. 0.400000 = 40%)
 * 'fixed'   — a fixed CHF amount (in split_rules_json.fixed — split_value is null)
 * 'tiered'  — tiered scale (split_rules_json carries the tier table)
 * 'mixed'   — mixed: per-recipient fixed + percent entries in split_rules_json
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.3 V3 variants A/B/C]
 */
export type SplitMethod = 'percent' | 'fixed' | 'tiered' | 'mixed';

/**
 * CobrokerBasis — the Bezugsbasis for the split computation (Kap. 10.3).
 *
 * 'gross_received' — the full vereinnahmte Courtage of the released line
 * 'net'            — after agreed deductions (encoded in split_rules_json.deductions)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.3]
 */
export type CobrokerBasis = 'gross_received' | 'net';

/**
 * CobrokerSplitStatus — the lifecycle status of a commission_split_line row (Kap. 10.6).
 *
 * 'pending'    — computed but not yet forwarded/received
 * 'payout_due' — Soll computed; payout is due from lead to recipient
 * 'paid'       — payout confirmed (Δ=0 or within rounding tolerance)
 * 'mismatch'   — payout confirmed but Δ≠0 → Beanstandung / Task raised
 * 'reclaimed'  — negative share from a Rückbelastung (Storno → mirrored reclaim)
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.5+10.6]
 */
export type CobrokerSplitStatus = 'pending' | 'payout_due' | 'paid' | 'mismatch' | 'reclaimed';

/**
 * CobrokerSplitClass — the per-role Differenzklasse driving follow-up (Kap. 10.7).
 *
 * lead role:
 *   'lead_underpaid'          — lead payout < Soll → open Verbindlichkeit + Korrektur-Task
 *   'lead_not_forwarded'      — vereinnahmt, keine Weiterleitung → Alert + Zahlungslauf-Task
 *
 * recipient role:
 *   'recipient_share_under'   — received < Soll → Forderung + Beanstandung
 *   'recipient_no_forwarding' — known lead Vereinnahmung but no Eingang → ausstehender Anteil
 *   'basis_difference'        — lead used a divergent Bemessungsbasis → Klärung + Transparenzlücke
 *
 * [Source: docs/concept/2026-06-23_Brokly_Detailkonzept_Courtageabrechnung_V3.txt#10.7]
 */
export type CobrokerSplitClass =
  | 'lead_underpaid'
  | 'lead_not_forwarded'
  | 'recipient_share_under'
  | 'recipient_no_forwarding'
  | 'basis_difference';

/**
 * BrokerRoleDto — one broker_role row (per-policy/mandate role for the own tenant).
 *
 * [Source: docs/stories/11.8-cobroker-split.md#Dev Notes — Data Models (broker_role)]
 */
export interface BrokerRoleDto {
  id:          string;
  tenant_id:   string;
  policy_id:   string | null;
  mandate_id:  string | null;
  broker_id:   string;
  role:        BrokerRoleKind;
  valid_from:  string | null;  // ISO date
  valid_to:    string | null;  // ISO date
  created_at:  string;
  updated_at:  string;
}

/**
 * CobrokerAgreementDto — one cobroker_agreement row as returned to the broker.
 *
 * split_value is a rate (e.g. '0.400000' = 40%) or null for 'fixed' (CHF in split_rules_json).
 *
 * [Source: docs/stories/11.8-cobroker-split.md#Dev Notes — Data Models (cobroker_agreement)]
 */
export interface CobrokerAgreementDto {
  id:                     string;
  tenant_id:              string;
  lead_broker_id:         string;
  recipient_broker_id:    string;
  scope:                  CobrokerScope;
  policy_id:              string | null;
  split_method:           SplitMethod;
  split_value:            string | null;   // numeric(8,6) as string; null for 'fixed'
  split_rules_json:       unknown | null;
  basis:                  CobrokerBasis;
  application_order:      number;
  commission_kind_filter: string | null;
  valid_from:             string | null;
  valid_to:               string | null;
  created_at:             string;
  updated_at:             string;
}

/**
 * CommissionSplitLineDto — one commission_split_line row (WORM).
 *
 * Finance-only CHF fields (basis_amount, expected_share, actual_share, delta_chf) are
 * null for non-Finance callers (D11-FIELD-LEVEL-FINANCE-ONLY — gated at READ time in
 * NestJS via applyCommissionSplitGate).
 *
 * [Source: docs/stories/11.8-cobroker-split.md#Dev Notes — Data Models (commission_split_line)]
 */
export interface CommissionSplitLineDto {
  id:                      string;                    // split_id (PK)
  tenant_id:               string;
  source_line_id:          string | null;             // nullable — recipient inbound has no carrier line
  cobroker_agreement_id:   string | null;
  role:                    BrokerRoleKind;            // lead | recipient
  counterparty_broker_id:  string;
  commission_kind:         string | null;

  // Finance-only amounts (nulled for non-Finance callers — D11-FIELD-LEVEL-FINANCE-ONLY):
  basis_amount:            string | null;             // numeric(14,2) as string
  expected_share:          string | null;             // Soll (SIGNED; negative for reclaim)
  actual_share:            string | null;             // Ist payout/received (nullable)
  delta_chf:               string | null;             // = actual − expected (SIGNED)

  direction:               string | null;             // 'none'|'underpayment'|'overpayment'
  split_class:             CobrokerSplitClass | null;
  match_level:             MatchLevel | null;
  status:                  CobrokerSplitStatus;
  payout_ref:              string | null;
  transparency_gap:        boolean;
  policy_version_id:       string | null;
  task_id:                 string | null;
  decision_reason:         string | null;
  findings:                unknown | null;
  created_at:              string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Story UI-U5: Courtage read DTOs (GET /commission/statements[:id]|akonto|splits|
//              expectations) — the thin reads that make E11 visible.
// ─────────────────────────────────────────────────────────────────────────────
//
// Convention delta vs the older E11 DTOs above: NO `tenant_id` on the wire
// (the token carries it — the V10-Q precedent), NO `holder_raw`/`blob_url` ever.
// Money = numeric strings; Finance-gated fields are nulled server-side via
// isFinanceAuthorized before serialization. Reuse the existing unions
// (MatchLevel / CommissionLineStatus / CommissionKind / SettlementType /
//  StatementSourceType / CommissionExpectationStatus / BrokerRoleKind /
//  CobrokerSplitStatus / CobrokerSplitClass); two new local unions below.
//
// [Source: docs/stories/ui-u5-courtage-tarifmatrix.md#Wire DTOs (Task 1)]

/**
 * CommissionDiffClassWire — the Differenzklasse as it appears on the courtage
 * read wire (the commission_diff_class column values; mirror the DB enum).
 */
export type CommissionDiffClassWire =
  | 'underpayment'
  | 'overpayment'
  | 'nicht_berechtigt'
  | 'fehlende_zeile'
  | 'strukturbruch';

/** AkontoStatusWire — the akonto_balance running-Saldo lifecycle values. */
export type AkontoStatusWire = 'offen' | 'teilverrechnet' | 'verrechnet' | 'ueberfaellig';

export interface CommissionStatementListItemDto {
  statement_id: string;
  carrier_uid: string | null;
  carrier_name: string | null;          // company_master join; null when unmatched
  period_from: string | null;           // ISO date
  period_to: string | null;
  currency: string;
  status: string;                       // received | header_ok | header_exception | duplicate (open text — map defensively)
  source_type: StatementSourceType;
  line_count: number;
  open_line_count: number;
  braucht_entscheid: boolean;           // server-computed
  soll_total: string | null;            // Finance-gated
  ist_total: string | null;             // Finance-gated
  delta_total: string | null;           // Finance-gated, signed
  check_score: string | null;
  created_at: string;
}

export interface CommissionLineViewDto {
  line_id: string;
  policy_id: string | null;
  policy_number: string | null;         // resolved policy; falls back to raw on the FE
  policy_number_raw: string | null;
  kunde_name: string | null;            // subject display name via policy join (never holder_raw)
  product_label: string | null;
  commission_kind: CommissionKind | null;
  settlement_type: SettlementType | null;
  line_period_from: string | null;
  line_period_to: string | null;
  ist: string | null;                   // line_total — Finance-gated
  soll: string | null;                  // latest soll.expected_amount — Finance-gated
  delta_chf: string | null;             // Finance-gated
  delta_pct: string | null;             // Finance-gated
  direction: 'none' | 'underpayment' | 'overpayment' | null;
  match_level: MatchLevel | null;       // latest exception (fallback: line.match_level)
  diff_class: CommissionDiffClassWire | null;
  line_status: CommissionLineStatus;    // latest exception.to_status, fallback line.status
  soll_status: string | null;           // computed | no_basis | no_tariff | akonto | …
  correction_group_id: string | null;
  is_akonto: boolean;                   // commission_kind === '06'
  // latest check result (Police-Identifikation + Berechtigung groups):
  check_status: string | null;          // matched | nicht_berechtigt | strukturbruch | no_match
  match_stage: string | null;
  mandate_valid: boolean | null;
  policy_active: boolean | null;
  broker_assigned: boolean | null;
  approved_tariff_exists: boolean | null;
  /** The latest check marked the line «nicht erwartet» (`courtage_not_eligible`); null without a check. Optional for older payloads. */
  courtage_not_eligible?: boolean | null;
  /** Its reason (one of the `courtage_window_*` codes); null when not marked or the check stored none. */
  courtage_not_eligible_reason?: CourtageNotEligibleReason | null;
  // Soll-Berechnung breakdown (Finance-gated):
  bemessung: string | null;
  applied_rate: string | null;
  applied_factor: string | null;
  // Nachweis pins + override surface:
  policy_version_id: string | null;
  tariff_id: string | null;
  override_reason: string | null;       // latest row_kind='override' reason (mandatory-reason surfacing)
  override_actor_kind: 'system' | 'human' | null;
  created_at: string;
}

export interface CommissionCorrectionGroupViewDto {
  id: string;
  policy_id: string | null;
  net_amount: string | null;            // Finance-gated
  net_expected: string | null;          // Finance-gated
  is_clean_correction: boolean;
  member_count: number;
}

export interface CommissionStatementDetailDto extends CommissionStatementListItemDto {
  broker_vu_code: string | null;
  broker_finma_nr: string | null;
  total_amount: string | null;          // ausgewiesenes Gesamttotal — Finance-gated
  sum_lines_ist: string | null;         // Σ Belegzeilen-Ist — Finance-gated
  kontrollsumme_ok: boolean;            // server-computed (0.05 tolerance; both-null → true)
  lines: CommissionLineViewDto[];
  correction_groups: CommissionCorrectionGroupViewDto[];
}

export interface AkontoBalanceViewDto {
  id: string;
  policy_id: string;
  policy_number: string | null;
  kunde_name: string | null;
  agreement_id: string | null;
  advanced_amount: string | null;       // Finance-gated
  settled_amount: string | null;        // Finance-gated
  open_balance: string | null;          // Finance-gated
  status: AkontoStatusWire;
  due_date: string | null;
  updated_at: string;
}

export interface CommissionSplitViewDto {
  id: string;
  role: BrokerRoleKind;                 // lead | recipient
  counterparty_broker_id: string;
  commission_kind: string | null;
  basis_amount: string | null;          // Finance-gated
  expected_share: string | null;        // Finance-gated
  actual_share: string | null;          // Finance-gated
  delta_chf: string | null;             // Finance-gated
  direction: string | null;
  split_class: CobrokerSplitClass | null;
  match_level: MatchLevel | null;
  status: CobrokerSplitStatus;
  transparency_gap: boolean;
  payout_ref: string | null;
  created_at: string;
}

export interface CommissionExpectationViewDto {
  id: string;
  policy_id: string;
  policy_number: string | null;
  period_from: string;
  period_to: string;
  commission_kind: string;
  expected_amount: string | null;       // Finance-gated
  status: CommissionExpectationStatus;
  due_date: string | null;
}

/**
 * The finding codes of the per-line check (`commission_line_check.findings`, codes only, no PII,
 * no amounts). `courtage_not_eligible`: the policy is linked to a Brokermandat registration and the
 * line's period lies outside its courtage window, or the line names no period at all.
 */
export type CommissionLineFindingCode =
  | 'header_exception'
  | 'vn_inconclusive'
  | 'vn_mismatch'
  | 'branche_mismatch'
  | 'branche_unverified'
  | 'no_version_at_period'
  | 'mandate_invalid'
  | 'policy_inactive'
  | 'policy_unassigned'
  | 'no_approved_tariff'
  | 'no_policy'
  | 'fuzzy_suggestion'
  | 'courtage_not_eligible'
  // P6: the line's period runs past the terminated mandate's Enddatum; accepted, reviewed by hand (no pro rata)
  | 'period_past_mandate_end'
  // the reason stored next to courtage_not_eligible (exactly one of them)
  | CourtageNotEligibleReason;

/** The line-check finding «nicht erwartet»: it marks the line, the matching runs on. */
export const COMMISSION_LINE_FINDING_COURTAGE_NOT_ELIGIBLE = 'courtage_not_eligible' satisfies CommissionLineFindingCode;

/**
 * German words for the line-check findings a screen shows. Only the findings with their own row in
 * the line detail are here; the others are shown through the check groups.
 */
export const COMMISSION_LINE_FINDING_LABELS: Readonly<Partial<Record<CommissionLineFindingCode, string>>> = {
  courtage_not_eligible: 'nicht erwartet (keine bestätigte Eintragung)',
  period_past_mandate_end: 'Periode über das Mandatsende hinaus: manuell prüfen',
};

/** The German words for «nicht erwartet» by its reason (the line detail picks one by the stored reason). */
export const COURTAGE_NOT_ELIGIBLE_REASON_LABELS: Readonly<Record<CourtageNotEligibleReason, string>> = {
  courtage_window_no_registration: 'nicht erwartet (keine bestätigte Eintragung)',
  courtage_window_not_started: 'nicht erwartet (Courtageberechtigung beginnt erst nach dieser Periode)',
  courtage_window_ended: 'nicht erwartet (Courtageberechtigung vor dieser Periode beendet)',
  courtage_window_no_period: 'nicht erwartet (Zeile ohne Abrechnungsperiode)',
};
